import React, { useState, useEffect } from "react";
import { TextField, Button, Box, Typography, Rating, createTheme, ThemeProvider } from "@mui/material";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

const theme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#1E90FF", // Light blue primary color
    },
    background: {
      default: "#000000", // Dark background color
      paper: "#000000", // Slightly lighter background for paper elements
    },
    text: {
      primary: "#ffffff", // Light text color
      secondary: "#b0b0b0", // Lighter text color for secondary text
    },
    error: {
      main: "#f44336", // Red for error messages
    },
  },
});

const FeedbackFormAcceptedRequests = ({ userId, requestId }) => {
  const [feedback, setFeedback] = useState({
    rating: 0,
    comments: "",
  });

  const [existingFeedback, setExistingFeedback] = useState(null); 
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false); 

  // Check if feedback already exists on component mount
  useEffect(() => {
    const checkFeedback = async () => {
      try {
        const response = await axios.post(`${API_URL}/check_feedback_accepted_requests/`, {
          request_id: requestId,
          user_id: userId,
        });

        if (response.status === 200 && response.data.rating !== null) {
          setExistingFeedback(response.data);
          setFeedback({
            rating: response.data.rating,
            comments: response.data.comments,
          });
        }
      } catch (err) {
        console.error("Error checking feedback:", err);
      }
    };

    checkFeedback();
  }, [requestId, userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFeedback({
      ...feedback,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (feedback.rating === 0) {
      setError("Please rate the session.");
      return;
    }

    if (feedback.comments.trim() === "") {
      setError("Please provide a comment.");
      return;
    }

    const data = {
      request_id: requestId,
      student_id: userId,
      student_rating: feedback.rating,
      student_comment: feedback.comments,
    };

    try {
      const response = await axios.post(`${API_URL}/create_user_feedback_accepted_requests/`, data);

      if (response.status === 200) {
        setSubmitted(true);
        setIsUpdating(false);
      } else {
        setError("Something went wrong, please try again later.");
      }
    } catch (err) {
      setError("Failed to submit feedback. Please try again.");
      console.error(err);
    }
  };

  const disableUpdateButton = existingFeedback !== null;

  return (
    <div>
      <ThemeProvider theme={theme}>
        <Box sx={{ maxWidth: 500, margin: "0 auto", padding: "20px", marginTop: "100px", borderRadius: "8px", boxShadow: 3, backgroundColor: "background.paper" }}>
          <Typography variant="h4" component="h1" gutterBottom color="text.primary" sx={{letterSpacing: 4}}>
            Session Feedback
          </Typography>

          {submitted ? (
            <Typography variant="h6" color="primary" sx={{letterSpacing: 3}}>
              Thank you for your feedback!
            </Typography>
          ) : existingFeedback ? (
            <Typography variant="h6" color="primary" sx={{letterSpacing: 3}}>
              Existing Feedback Found. You can update it.
            </Typography>
          ) : (
            <Typography variant="h6" color="primary" sx={{letterSpacing: 3}}>
              No feedback found. Please provide your feedback.
            </Typography>
          )}

          <form onSubmit={handleSubmit}>
            <Typography variant="body1" sx={{ marginBottom: "0.5rem", letterSpacing: 3 }} color="text.primary">
              How would you rate the session?
            </Typography>
            <Rating
              name="rating"
              value={feedback.rating}
              onChange={(_, newValue) => handleChange({ target: { name: "rating", value: newValue } })}
              precision={0.5}
              sx={{ marginBottom: "1rem", letterSpacing: 3 }}
            />

<TextField
  label="Comments"
  name="comments"
  value={feedback.comments}
  onChange={handleChange}
  fullWidth
  multiline
  rows={4}
  inputProps={{ maxLength: 100 }} // Limit input to 100 characters
  helperText={`${feedback.comments.length}/100 characters`} // Show character count
  sx={{ marginBottom: "1rem", letterSpacing: 3 }}
  InputLabelProps={{
    style: { color: "white", letterSpacing: 3 },
  }}
  InputProps={{
    style: { color: "white", letterSpacing: 3 },
  }}
/>


            {error && (
              <Typography variant="body2" color="error" sx={{ marginBottom: "1rem", letterSpacing: 3 }}>
                {error}
              </Typography>
            )}

            <Button
              variant="contained"
              color="primary"
              type="submit"
              fullWidth
              sx={{letterSpacing: 4}}
              disabled={disableUpdateButton}
            >
              {existingFeedback ? "Update Feedback" : "Submit Feedback"}
            </Button>
          </form>
        </Box>
      </ThemeProvider>
    </div>
  );
};

export default FeedbackFormAcceptedRequests;
